/**
 * Service d'authentification
 */

import { api, ApiError } from './api'
import { API_ENDPOINTS, buildApiUrl } from '@/config/api'
import type {
  LoginRequest,
  LoginResponse,
  VerifyOTPRequest,
  VerifyOTPResponse,
  User,
} from '@/types/auth'

/**
 * Demande un code OTP pour l'authentification
 */
export const requestOTP = async (email: string): Promise<LoginResponse> => {
  const request: LoginRequest = { email: email.toLowerCase().trim() }

  const response = await api.post<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, request)

  return response.data
}

/**
 * Vérifie le code OTP et récupère le token d'authentification
 */
export const verifyOTP = async (
  email: string,
  otpCode: string
): Promise<VerifyOTPResponse> => {
  const request: VerifyOTPRequest = {
    email: email.toLowerCase().trim(),
    otp_code: otpCode.trim(),
  }

  const response = await api.post<VerifyOTPResponse>(
    API_ENDPOINTS.AUTH.VERIFY_OTP,
    request
  )

  if (response.data.access_token) {
    localStorage.setItem('access_token', response.data.access_token)
    localStorage.setItem('user', JSON.stringify(response.data.user))
  }

  return response.data
}

/**
 * Échange un token de session Clerk contre le JWT applicatif.
 * Fetch dédié : évite d'écraser Authorization et le redirect 401 global.
 */
export const loginWithClerkToken = async (
  clerkSessionToken: string
): Promise<VerifyOTPResponse> => {
  const url = buildApiUrl(API_ENDPOINTS.AUTH.CLERK)
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${clerkSessionToken}`,
    },
  })

  const contentType = response.headers.get('content-type')
  const isJson = contentType?.includes('application/json')
  const payload = isJson ? await response.json().catch(() => null) : null

  if (!response.ok) {
    const detail =
      payload && typeof payload === 'object' && 'detail' in payload
        ? String((payload as { detail: unknown }).detail)
        : `Erreur ${response.status}`
    throw new ApiError(detail, response.status, payload)
  }

  const data = payload as VerifyOTPResponse
  if (data?.access_token) {
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('user', JSON.stringify(data.user))
  }

  return data
}

/**
 * Déconnexion (supprime le token du localStorage)
 */
export const logout = async (): Promise<void> => {
  try {
    await api.post(API_ENDPOINTS.AUTH.LOGOUT)
  } catch (error) {
    console.error('Erreur lors de la déconnexion:', error)
  } finally {
    localStorage.removeItem('access_token')
    localStorage.removeItem('user')
  }
}

export const getAuthToken = (): string | null => {
  return localStorage.getItem('access_token')
}

export const getCurrentUserFromStorage = (): User | null => {
  const userStr = localStorage.getItem('user')
  if (!userStr) return null

  try {
    return JSON.parse(userStr)
  } catch {
    return null
  }
}

export const fetchCurrentUser = async (): Promise<User> => {
  const response = await api.get<User>(API_ENDPOINTS.AUTH.ME)

  if (response.data) {
    localStorage.setItem('user', JSON.stringify(response.data))
  }

  return response.data
}

export const isAuthenticated = (): boolean => {
  return !!getAuthToken()
}
